import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MessageSquarePlus, Star, ImagePlus, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";

const MAX_PHOTO = 5 * 1024 * 1024; // 5 MB

function Stars({ value, onChange, readOnly = false, size = "w-7 h-7" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange?.(n)}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            className={
              readOnly
                ? "cursor-default"
                : "cursor-pointer transition-transform hover:scale-110"
            }
            aria-label={`${n}`}
            data-testid={`review-star-${n}`}
          >
            <Star
              className={`${size} ${
                active ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function ReviewDialog() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const fileRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const loadReviews = useCallback(async () => {
    try {
      const res = await fetch(`${backend}/api/reviews`);
      if (res.ok) setReviews(await res.json());
    } catch {
      /* noop */
    }
  }, [backend]);

  useEffect(() => {
    if (open) loadReviews();
  }, [open, loadReviews]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_PHOTO) {
      toast.error(t("reviews.error"));
      return;
    }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setRating(0);
    setName("");
    setComment("");
    setPhoto(null);
    setPhotoPreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (rating < 1) {
      toast.error(t("reviews.ratingRequired"));
      return;
    }
    if (!comment.trim()) {
      toast.error(t("reviews.commentRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("rating", String(rating));
      fd.append("comment", comment.trim());
      if (name.trim()) fd.append("name", name.trim());
      fd.append("lang", (i18n.resolvedLanguage || "es").slice(0, 2));
      if (photo) fd.append("photo", photo);
      const res = await fetch(`${backend}/api/reviews`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("failed");
      toast.success(t("reviews.success"));
      reset();
      loadReviews();
    } catch {
      toast.error(t("reviews.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <motion.button
          type="button"
          data-testid="review-open-button"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: 1,
            scale: [1, 1.05, 1],
            boxShadow: [
              "0 0 0 0 rgba(124,108,255,0.55)",
              "0 0 0 10px rgba(124,108,255,0)",
              "0 0 0 0 rgba(124,108,255,0)",
            ],
          }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.95 }}
          className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full bg-gradient-to-r from-primary to-fuchsia-500 text-primary-foreground text-xs sm:text-sm font-semibold shadow-lg shadow-primary/30 whitespace-nowrap"
        >
          <MessageSquarePlus className="w-4 h-4 shrink-0" />
          {t("reviews.button")}
        </motion.button>
      </DialogTrigger>

      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("reviews.title")}</DialogTitle>
          <DialogDescription>{t("reviews.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t("reviews.rating")}
            </label>
            <Stars value={rating} onChange={setRating} />
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t("reviews.name")}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="review-name"
              className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t("reviews.comment")}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder={t("reviews.commentPlaceholder")}
              data-testid="review-comment"
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPhoto}
              className="hidden"
              data-testid="review-photo-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="review-add-photo"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary text-sm font-medium hover:brightness-110 transition"
            >
              <ImagePlus className="w-4 h-4" />
              {photoPreview ? t("reviews.changePhoto") : t("reviews.addPhoto")}
            </button>
            {photoPreview && (
              <img
                src={photoPreview}
                alt="preview"
                className="mt-3 h-28 rounded-lg object-cover ring-1 ring-border"
                data-testid="review-photo-preview"
              />
            )}
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="review-submit"
            className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-primary text-primary-foreground font-semibold hover:brightness-110 transition disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {submitting ? t("reviews.submitting") : t("reviews.submit")}
          </button>

          <div className="pt-3 border-t border-border/60">
            <h4 className="text-sm font-semibold mb-2">{t("reviews.recent")}</h4>
            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="review-empty">
                {t("reviews.empty")}
              </p>
            ) : (
              <ul className="space-y-3" data-testid="review-list">
                {reviews.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-lg border border-border/60 bg-card/50 p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {r.name || t("reviews.anonymous")}
                      </span>
                      <Stars value={r.rating} readOnly size="w-4 h-4" />
                    </div>
                    {r.comment && (
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {r.comment}
                      </p>
                    )}
                    {r.photo && (
                      <img
                        src={r.photo}
                        alt="review"
                        className="mt-2 h-24 rounded-md object-cover ring-1 ring-border"
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
